/**
 * Headless selectors + formatters for the Insights → Top 100 Customers tab.
 *
 * Platform-neutral (no DOM, no recharts) per the shared-core contract. The
 * web-shell chart components consume these to derive the scatter points, the
 * Pareto series, and the watch-list from the server-ranked rows. The server
 * has already applied the Top-N cut — these are pure projections, no sorting
 * or slicing of the underlying league table.
 */

import type { InsightsTopCustomerRow } from "../data/api-types";
import { formatGbpCompact } from "./insights-monthly-revenue";

export { formatGbpCompact };

/** One customer projected for the scale-vs-momentum scatter. */
export interface TopCustomerScatterPoint {
  code: string;
  name: string;
  /** 12-month revenue (x-axis, log-scaled by the chart). */
  revenueTtm: number;
  /** Last-90-day revenue (bubble size). */
  revenue90d: number;
  /** Year-on-year growth %, derived from prior TTM. null when no prior window. */
  yoyPct: number | null;
  /** Contribution margin %, 0..100. null when no TTM revenue. */
  marginPct: number | null;
  /** Head-account segment (strategic/key/client), drives the scatter filter. */
  segment: string | null;
  /** Owning sales rep (tooltip). */
  rep: string | null;
  /** Market (tooltip). */
  market: string | null;
  /** Quarter-on-quarter growth % (last-90 vs previous-90). Tooltip. */
  qoqPct: number | null;
}

/**
 * What a Pareto share is measured against: the bars shown (so the line always
 * ends at 100%) or whole-company TTM revenue (so it ends at the shown set's
 * true share of the business).
 */
export type ShareBasis = "shown" | "total";

export const SHARE_BASES = [
  { value: "shown", label: "Share of top 20" },
  { value: "total", label: "Share of total revenue" },
] as const satisfies readonly { value: ShareBasis; label: string }[];

/** One bar of the revenue Pareto, with its share on both bases. */
export interface ParetoBar {
  code: string;
  name: string;
  revenueTtm: number;
  /** This customer's share (0..1) of the shown bars' revenue. */
  shareOfShown: number;
  /** This customer's share (0..1) of whole-company revenue; null when unknown. */
  shareOfTotal: number | null;
  /** Running share (0..1) of the shown bars, up to and including this bar. */
  cumulativeShareOfShown: number;
  /** Running share (0..1) of whole-company revenue; null when unknown. */
  cumulativeShareOfTotal: number | null;
}

/** One watch-list row — big accounts softening year-on-year. */
export interface WatchListRow {
  code: string;
  name: string;
  revenueTtm: number;
  yoyPct: number;
  marginPct: number | null;
  /** Quarter-on-quarter growth % (last-90 vs previous-90). null when no window. */
  qoqPct: number | null;
  /** Owning sales rep. null when the head account has no sales-rep portfolio. */
  rep: string | null;
}

/**
 * Year-on-year growth % from matched TTM windows. null when there is no prior
 * revenue to compare against (a new account) — those are omitted from the
 * momentum scatter, per the design.
 */
export function yoyPct(row: InsightsTopCustomerRow): number | null {
  if (!row.revenue_prior_ttm) return null;
  return (
    (100 * (row.revenue_ttm - row.revenue_prior_ttm)) / row.revenue_prior_ttm
  );
}

/** Contribution margin % (0..100) from the 0..1 fraction; null when absent. */
export function marginPct(row: InsightsTopCustomerRow): number | null {
  return row.margin_pct_ttm == null ? null : row.margin_pct_ttm * 100;
}

/** Project each customer to a scatter point (all rows; the chart filters nulls). */
export function toScatterPoints(
  rows: InsightsTopCustomerRow[],
): TopCustomerScatterPoint[] {
  return rows.map((r) => ({
    code: r.customer_code,
    name: r.customer_name,
    revenueTtm: r.revenue_ttm,
    revenue90d: r.revenue_last_90_days,
    yoyPct: yoyPct(r),
    marginPct: marginPct(r),
    segment: r.customer_segment ?? null,
    rep: r.sales_rep_name ?? null,
    market: r.market ?? null,
    qoqPct: r.qoq_pct ?? null,
  }));
}

/**
 * Top-``n`` bars by TTM revenue, each carrying its individual and running
 * share against both the shown set and whole-company revenue
 * (``companyRevenueTtm``; a non-positive total yields null "of total" shares).
 * Rows arrive already ranked from the server; this takes the leading ``n`` —
 * no re-sort.
 */
export function toParetoBars(
  rows: InsightsTopCustomerRow[],
  companyRevenueTtm: number,
  n = 20,
): ParetoBar[] {
  const shown = rows.slice(0, n);
  const shownTotal = shown.reduce((s, r) => s + r.revenue_ttm, 0) || 1;
  const ofTotal = (v: number) =>
    companyRevenueTtm > 0 ? v / companyRevenueTtm : null;
  let running = 0;
  return shown.map((r) => {
    running += r.revenue_ttm;
    return {
      code: r.customer_code,
      name: r.customer_name,
      revenueTtm: r.revenue_ttm,
      shareOfShown: r.revenue_ttm / shownTotal,
      shareOfTotal: ofTotal(r.revenue_ttm),
      cumulativeShareOfShown: running / shownTotal,
      cumulativeShareOfTotal: ofTotal(running),
    };
  });
}

/**
 * Big-and-shrinking watch list: accounts over ``minRevenue`` that are down
 * year-on-year, keeping the server's revenue order. The intervention list.
 */
export function toWatchList(
  rows: InsightsTopCustomerRow[],
  minRevenue = 100_000,
): WatchListRow[] {
  const out: WatchListRow[] = [];
  for (const r of rows) {
    const yoy = yoyPct(r);
    if (r.revenue_ttm >= minRevenue && yoy != null && yoy < 0) {
      out.push({
        code: r.customer_code,
        name: r.customer_name,
        revenueTtm: r.revenue_ttm,
        yoyPct: yoy,
        marginPct: marginPct(r),
        qoqPct: r.qoq_pct ?? null,
        rep: r.sales_rep_name ?? null,
      });
    }
  }
  return out;
}

/** Total TTM revenue across the shown customers (KPI). */
export function totalRevenueTtm(rows: InsightsTopCustomerRow[]): number {
  return rows.reduce((s, r) => s + r.revenue_ttm, 0);
}

/** Revenue of the leading ``n`` rows (rows arrive server-ranked). */
export function topNRevenue(rows: InsightsTopCustomerRow[], n = 10): number {
  return rows.slice(0, n).reduce((s, r) => s + r.revenue_ttm, 0);
}

/**
 * Top-``n`` revenue as a share (0..1) of ``denominator`` — pass the shown set's
 * total for "share of top 100", or company TTM revenue for "share of total".
 */
export function topNShare(
  rows: InsightsTopCustomerRow[],
  denominator: number,
  n = 10,
): number {
  return denominator > 0 ? topNRevenue(rows, n) / denominator : 0;
}

/** Count of steep decliners (YoY below ``threshold`` %, default −40). */
export function steepDeclinerCount(
  rows: InsightsTopCustomerRow[],
  threshold = -40,
): number {
  return rows.filter((r) => {
    const y = yoyPct(r);
    return y != null && y < threshold;
  }).length;
}

/** Count of strong growers (YoY above ``threshold`` %, default +50). */
export function strongGrowerCount(
  rows: InsightsTopCustomerRow[],
  threshold = 50,
): number {
  return rows.filter((r) => {
    const y = yoyPct(r);
    return y != null && y > threshold;
  }).length;
}

/** Count gone quiet — no order in over ``threshold`` days (default 90). */
export function goneQuietCount(
  rows: InsightsTopCustomerRow[],
  threshold = 90,
): number {
  return rows.filter(
    (r) =>
      r.days_since_last_order != null && r.days_since_last_order > threshold,
  ).length;
}

/** Count margin-eroding — margin-move below ``threshold`` pp (default −10). */
export function marginErodingCount(
  rows: InsightsTopCustomerRow[],
  threshold = -10,
): number {
  return rows.filter(
    (r) => r.margin_move_pp != null && r.margin_move_pp < threshold,
  ).length;
}

/** Scatter segment-filter options — All plus the mockup's three tiers. */
export const SEGMENT_FILTERS = [
  { value: "all", label: "All" },
  { value: "strategic", label: "Strategic" },
  { value: "key", label: "Key" },
  { value: "client", label: "Client" },
] as const;

export type SegmentFilter = (typeof SEGMENT_FILTERS)[number]["value"];

/** Signed percent for labels — "+12%" / "−13%". */
export function formatSignedPct(n: number): string {
  const sign = n > 0 ? "+" : n < 0 ? "−" : "";
  return `${sign}${Math.round(Math.abs(n))}%`;
}
